import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';
import { Site, SiteInput } from '../../types/asset.types';
import { ApiEndpoints } from '../../services/ApiUrls'; // Adjust the path accordingly

export interface SiteUpdateInput extends Partial<SiteInput> {
  updateReason?: string;
}

export interface SiteDeleteInput {
  id: string;
}

export interface SiteDeleteResponse {
  success?: boolean;
  message?: string;
  error?: string;
}

export interface SiteFilter {
  search?: string;
  countryId?: string;
  stateId?: string;
  activeOnly?: boolean;
  dropdown?: boolean;
  page?: number;
  limit?: number;
  ordering?: string;
  searchParams?: Record<string, string | boolean>,
  [key: string]: any; // Allow additional arbitrary filters
}

export interface SitePaginatedResponse {
  count: number;
  previous: string;
  next: string;
  results: Site[];
}

export const siteApi = createApi({
  reducerPath: 'siteApi',
  baseQuery: createExtendedTokenBaseQuery(''),
  tagTypes: ['Site'],
  endpoints: (builder) => ({
    getSites: builder.query<SitePaginatedResponse, SiteFilter>({
      query: (filter = {}) => {
        const params = new URLSearchParams();
        Object.entries(filter).forEach(([key, value]) => {
        if (
          value === undefined ||
          value === null ||
          value === ''
        ) {
          return;
        }

        if (key === 'searchParams') {
          return;
        }

        // Handle arrays if needed
        if (Array.isArray(value)) {
          value.forEach((item) =>
            params.append(key, String(item))
          );
          return;
        }

        params.append(key, String(value));
      });

      // Handle nested searchParams
      if (
        filter.searchParams &&
        Object.keys(filter.searchParams).length > 0
      ) {
        Object.entries(filter.searchParams).forEach(
          ([key, value]) => {
            if (
              value !== undefined &&
              value !== null &&
              value !== ''
            ) {
              params.append(key, String(value));
            }
          }
        );
      }
        return { url: ApiEndpoints.siteEndpoints.sites.list, params };
      },
      providesTags: ['Site'],
    }),

    // New dropdown-specific endpoint
    getSitesDropdown: builder.query<SitePaginatedResponse, SiteFilter | void>({
      query: (filter = {}) => {
        const params = new URLSearchParams();
        if (filter) {
          if (filter.search) params.append('search', filter.search);
          if (filter.countryId) params.append('country', filter.countryId);
          if (filter.stateId) params.append('state', filter.stateId);
          if (filter.page !== undefined) params.append('page', filter.page.toString());
          if (filter.limit !== undefined) params.append('limit', filter.limit.toString());
          if (filter.ordering && filter.ordering.trim() !== '') params.append('ordering', filter.ordering);
          if (filter.searchParams  && Object.keys(filter.searchParams).length > 0) {
            Object.entries(filter.searchParams).forEach(([key, value]) => {
              if (value) params.append(key, value.toString());
            });
          }
        }
        return { url: 'master/sites/dropdown/', params };
      },
      providesTags: ['Site'],
    }),

    getSiteById: builder.query<Site, string>({
      query: (id) => ApiEndpoints.siteEndpoints.sites.detail(id),
      providesTags: (result, error, id) => [{ type: 'Site', id }],
    }),

    createSite: builder.mutation<Site, SiteInput>({
      query: (site) => ({
        url: ApiEndpoints.siteEndpoints.sites.list,
        method: 'POST',
        body: site,
      }),
      invalidatesTags: ['Site'],
    }),

    updateSite: builder.mutation<Site, { id: string; site: SiteUpdateInput; changeReason?: string }>({
      query: ({ id, site, changeReason }) => ({
        url: ApiEndpoints.siteEndpoints.sites.detail(id),
        method: 'PATCH',
        body: { ...site, change_reason: changeReason },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Site', id }, 'Site'],
    }),
    deleteSite: builder.mutation<SiteDeleteResponse, SiteDeleteInput>({
      query: ({ id}) => ({
        url: ApiEndpoints.siteEndpoints.sites.detail(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['Site'],
    }),
  }),
});

export const {
  useGetSitesQuery,
  useGetSitesDropdownQuery,
  useLazyGetSitesQuery,
  useGetSiteByIdQuery,
  useCreateSiteMutation,
  useUpdateSiteMutation,
  useDeleteSiteMutation,
} = siteApi;